/**
 * Point Cloud Viewer Component
 * Displays pre-converted point clouds via Potree viewer iframe.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useApp } from '../../../store/AppContext';
import { api } from '../../../services/api';
import './PointCloudViewer.css';

export const PointCloudViewer: React.FC = () => {
  const { state, updateProject } = useApp();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // UI state
  const [isLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);

  // Material/rendering state
  const [currentMaterial, setCurrentMaterial] = useState<string>('rgba');
  // Modes the loaded cloud can actually display (null until the viewer reports them)
  const [availableMaterials, setAvailableMaterials] = useState<string[] | null>(null);

  const materialOptions = [
    { id: 'rgba', label: 'RGB Color' },
    { id: 'intensity', label: 'Intensity' },
    { id: 'elevation', label: 'Elevation' },
    { id: 'classification', label: 'Classification' },
  ];

  // Get current project and point clouds
  const project = state.currentProject;
  const pointClouds = project?.pointClouds || [];
  const selectedId = project?.preferences?.selectedPointCloudId;
  const selectedPointCloud = pointClouds.find((pc) => pc.id === selectedId);

  // Auto-select first point cloud if none is selected or selection is stale
  useEffect(() => {
    if (!project || pointClouds.length === 0) return;
    const selectionValid = selectedId && pointClouds.some((pc) => pc.id === selectedId);
    if (selectionValid) return;
    updateProject(project.id, {
      preferences: { ...project.preferences, selectedPointCloudId: pointClouds[0].id },
    });
  }, [project?.id, pointClouds.length, selectedId]);

  // Load viewer when point cloud is selected
  useEffect(() => {
    if (!selectedPointCloud) return;

    setAvailableMaterials(null);

    // COPC files load directly
    if (selectedPointCloud.copcUrl) {
      setError(null);
      setViewerUrl(api.potree.getCopcViewerUrl(selectedPointCloud.copcUrl));
      return;
    }

    // Load from cache hash
    if (selectedPointCloud.cacheHash) {
      setError(null);
      setViewerUrl(api.potree.getViewerUrl(selectedPointCloud.cacheHash));
      return;
    }

    setError('This point cloud has not been converted yet. Use the desktop converter to process it.');
  }, [selectedPointCloud?.id]);

  // Handle selecting a point cloud
  const handleSelectPointCloud = async (id: string) => {
    if (!project) return;
    await updateProject(project.id, {
      preferences: { ...project.preferences, selectedPointCloudId: id },
    });
  };

  // Format point count
  const formatPoints = (count?: number) => {
    if (!count) return '';
    if (count < 1000) return count.toString();
    if (count < 1000000) return `${(count / 1000).toFixed(1)}K`;
    return `${(count / 1000000).toFixed(1)}M`;
  };

  // The viewer reports the attribute it picked on load (elevation for clouds without RGB)
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type === 'materialChanged' && typeof event.data.material === 'string') {
        setCurrentMaterial(event.data.material);
      }
      if (event.data?.type === 'materialsAvailable' && Array.isArray(event.data.materials)) {
        setAvailableMaterials(event.data.materials.filter((m: unknown): m is string => typeof m === 'string'));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Send message to Potree iframe
  const sendToViewer = useCallback((message: { type: string; [key: string]: unknown }) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  }, []);

  // Handle material change
  const handleMaterialChange = (material: string) => {
    setCurrentMaterial(material);
    sendToViewer({ type: 'setMaterial', material });
  };

  // Render empty state
  if (!project) {
    return (
      <div className="pointcloud-viewer">
        <div className="pointcloud-empty">
          <div className="pointcloud-empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="1" />
              <circle cx="8" cy="8" r="1" />
              <circle cx="16" cy="8" r="1" />
              <circle cx="8" cy="16" r="1" />
              <circle cx="16" cy="16" r="1" />
              <circle cx="4" cy="12" r="1" />
              <circle cx="20" cy="12" r="1" />
              <circle cx="12" cy="4" r="1" />
              <circle cx="12" cy="20" r="1" />
            </svg>
          </div>
          <h3>No Project Selected</h3>
          <p>Select a project to view point clouds</p>
        </div>
      </div>
    );
  }

  if (pointClouds.length === 0) {
    return (
      <div className="pointcloud-viewer">
        <div className="pointcloud-empty">
          <div className="pointcloud-empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="1" />
              <circle cx="8" cy="8" r="1" />
              <circle cx="16" cy="8" r="1" />
              <circle cx="8" cy="16" r="1" />
              <circle cx="16" cy="16" r="1" />
              <circle cx="4" cy="12" r="1" />
              <circle cx="20" cy="12" r="1" />
              <circle cx="12" cy="4" r="1" />
              <circle cx="12" cy="20" r="1" />
            </svg>
          </div>
          <h3>No Point Clouds</h3>
          <p>No point clouds available for this project</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pointcloud-viewer">
      {/* Floating toolbar */}
      <div className="pointcloud-toolbar">
        <div className="pointcloud-toolbar-left">
          <div className="pointcloud-select-wrapper">
            <svg className="pointcloud-select-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
            <select
              className="pointcloud-select"
              value={selectedId || ''}
              onChange={(e) => handleSelectPointCloud(e.target.value)}
            >
              {pointClouds.map((pc) => (
                <option key={pc.id} value={pc.id}>
                  {pc.name} {pc.pointCount ? `(${formatPoints(pc.pointCount)} pts)` : ''}
                </option>
              ))}
            </select>
            <svg className="pointcloud-select-arrow" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 10l5 5 5-5H7z"/>
            </svg>
          </div>

          {/* Material selector */}
          {viewerUrl && !isLoading && (
            <div className="pointcloud-select-wrapper pointcloud-material-select">
              <svg className="pointcloud-select-icon" viewBox="2 2 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5" />
                <path d="M12 3v2M12 19v2M5.64 5.64l1.41 1.41M16.95 16.95l1.41 1.41M3 12h2M19 12h2M5.64 18.36l1.41-1.41M16.95 7.05l1.41-1.41" />
              </svg>
              <select
                className="pointcloud-select"
                value={currentMaterial}
                onChange={(e) => handleMaterialChange(e.target.value)}
                title="Rendering Mode"
              >
                {materialOptions.filter((opt) => !availableMaterials || availableMaterials.includes(opt.id)).map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <svg className="pointcloud-select-arrow" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5H7z"/>
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Main viewer area */}
      <div className="pointcloud-main">
        {/* Loading state */}
        {isLoading && (
          <div className="pointcloud-loading">
            <div className="pointcloud-spinner"></div>
            <p className="pointcloud-message">Loading point cloud...</p>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="pointcloud-error">
            <div className="pointcloud-error-icon">!</div>
            <h3>Failed to load point cloud</h3>
            <p>{error}</p>
            <button
              className="pointcloud-retry-btn"
              onClick={() => {
                setError(null);
                if (selectedPointCloud) {
                  handleSelectPointCloud(selectedPointCloud.id);
                }
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Potree viewer iframe */}
        {!isLoading && !error && selectedPointCloud && viewerUrl && (
          <iframe
            ref={iframeRef}
            src={viewerUrl}
            className="pointcloud-iframe"
            title="Potree Point Cloud Viewer"
            allow="fullscreen; accelerometer; gyroscope; magnetometer; xr-spatial-tracking; geolocation"
          />
        )}

        {/* No selection state */}
        {!isLoading && !error && !selectedPointCloud && (
          <div className="pointcloud-no-selection">
            <p>Select a point cloud from the list</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PointCloudViewer;
